import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ShieldCheck, ShoppingBag, Store } from "lucide-react";
import { listMyOrders } from "@/lib/orders.functions";
import { deliveryLabel, kr, statusLabel, statusTone } from "@/lib/orders";
import { adImageSrc } from "@/lib/image-src";

export const Route = createFileRoute("/_authenticated/mina-affarer")({
  component: MyOrders,
  head: () => ({
    meta: [
      { title: "Mina köp och sälj – Sellavo" },
      {
        name: "description",
        content: "Följ dina trygga affärer: betalning, frakt och godkännande på ett ställe.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
});

function MyOrders() {
  const fetchOrders = useServerFn(listMyOrders);
  const [tab, setTab] = useState<"purchases" | "sales">("purchases");
  const { data, isLoading } = useQuery({ queryKey: ["my-orders"], queryFn: () => fetchOrders({}) });

  const rows = (tab === "purchases" ? data?.purchases : data?.sales) ?? [];

  return (
    <main className="mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8 pb-16 pt-6 sm:pb-20 sm:pt-10">
      <h1 className="inline-flex items-center gap-2 font-heading text-2xl font-bold sm:text-3xl">
        <ShieldCheck className="h-6 w-6 text-primary" /> Mina köp och sälj
      </h1>
      <p className="mt-1 text-muted-foreground">
        Alla affärer där betalningen går via oss. Klicka in för fraktsedel och godkännande.
      </p>

      <div className="mt-6 inline-flex rounded-lg bg-muted p-1">
        {(
          [
            ["purchases", "Mina köp", ShoppingBag],
            ["sales", "Mina sälj", Store],
          ] as const
        ).map(([key, label, Icon]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
              tab === key ? "bg-background shadow-sm" : "text-muted-foreground"
            }`}
          >
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      {isLoading && <p className="mt-6 text-muted-foreground">Laddar…</p>}

      {!isLoading && rows.length === 0 && (
        <p className="mt-6 rounded-xl border border-dashed border-border p-8 text-center text-muted-foreground">
          Inga affärer här ännu.
        </p>
      )}

      <ul className="mt-6 space-y-3">
        {rows.map((o: any) => (
          <li key={o.id}>
            <Link
              to="/order/$id"
              params={{ id: o.id }}
              className="flex items-center gap-4 rounded-xl border border-border bg-card p-3 transition-colors hover:bg-muted/50"
            >
              {o.ad_image ? (
                <img
                  src={adImageSrc(o.ad_image)}
                  alt={o.ad_title}
                  className="h-16 w-16 shrink-0 rounded-lg object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="h-16 w-16 shrink-0 rounded-lg bg-muted" />
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{o.ad_title}</p>
                <p className="text-sm text-muted-foreground">
                  {deliveryLabel(o.delivery_method)} ·{" "}
                  {tab === "purchases" ? kr(o.total_amount) : kr(o.payout_amount)}
                </p>
              </div>
              <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${statusTone(o.status)}`}>
                {statusLabel(o.status)}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </main>
  );
}
